import { faAngleLeft, faAngleRight, faRightFromBracket } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { IRouter, routes } from '../../Routes/routes';
import "./Sidebar.css"
import { useContext, useState } from 'react';
import { RolesContext } from '../../Routes/Private.context';

// Nav grouping is presentation-only: it maps each route value to a section
// label. It does NOT alter routes, paths, roles, or navigation behaviour.
const NAV_GROUPS: { label: string; routes: string[] }[] = [
    { label: "Overview", routes: ["dashboard"] },
    { label: "Content", routes: ["view", "upload", "auto-upload", "subject", "exam_category"] },
    { label: "Generation", routes: ["qp_generation", "ai-generator", "download"] },
    { label: "Review", routes: ["qa", "ai-qa", "translator"] },
    { label: "Admin", routes: ["user", "requests", "apiKey"] },
];

// Per-route accent colour — drives the colourful icon chips + active state.
// Presentation only; falls back to a cycling palette for any new route.
const NAV_COLORS: Record<string, string> = {
    dashboard: "#2563eb",
    view: "#0891b2",
    upload: "#16a34a",
    "auto-upload": "#0d9488",
    download: "#7c3aed",
    qp_generation: "#db2777",
    exam_category: "#d97706",
    subject: "#ea580c",
    qa: "#16a34a",
    "ai-generator": "#8b5cf6",
    "ai-qa": "#6366f1",
    requests: "#475569",
    user: "#0ea5e9",
    translator: "#14b8a6",
    apiKey: "#f43f5e",
};
const FALLBACK_PALETTE = ["#2563eb", "#0891b2", "#16a34a", "#7c3aed", "#d97706", "#db2777", "#14b8a6"];
const colorFor = (route: string, index: number) =>
    NAV_COLORS[route] ?? FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];

export default function Sidebar() {
    let navigate = useNavigate();
    const location = useLocation()

    const { roles, getUserRoles } = useContext(RolesContext);
    const { params } = useParams();

    const [collapsed, setCollapsed] = useState<boolean>(false);

    const checkIfRouteAllowed = (path: string) => {
        const allowed = roles?.find(((data: any) => data.role === path && data.allowed === true))
        return allowed ? true : false;
    }

    // All nav-visible, role-allowed routes (gating logic unchanged from before)
    const visibleRoutes = routes.filter(
        (data: IRouter) => data.navShow === true && checkIfRouteAllowed(data.role)
    );

    const isActive = (route: string) =>
        location.pathname.split("/").find((path: string) => path === route) ? true : false;

    // Build ordered groups, appending any ungrouped item to a fallback group so
    // nothing is ever hidden.
    const grouped = NAV_GROUPS.map((group) => ({
        label: group.label,
        items: visibleRoutes.filter((r: IRouter) => group.routes.includes(r.route)),
    }));
    const knownRoutes = NAV_GROUPS.flatMap((g) => g.routes);
    const ungrouped = visibleRoutes.filter((r: IRouter) => !knownRoutes.includes(r.route));
    if (ungrouped.length) grouped.push({ label: "More", items: ungrouped });

    const renderItem = (data: IRouter, index: number) => (
        <div
            key={data.route}
            className={`ds-nav-item xcn-link-pointer ${isActive(data.route) ? "is-active" : ""}`}
            style={{ ["--item-color" as any]: colorFor(data.route, index) }}
            title={collapsed ? data.name : undefined}
            onClick={() => navigate("/dash/" + data.route)}
        >
            <span className="ds-nav-item__icon">
                <FontAwesomeIcon icon={data.icon} />
            </span>
            <span className="ds-nav-item__label">{data.name}</span>
        </div>
    );

    return (
        <div className={`ds-sidebar ${collapsed ? "ds-sidebar--collapsed" : "ds-sidebar--expanded"}`}>
            <div className="ds-sidebar__brand">
                <span className="ds-sidebar__brand-mark xcn-link-pointer" onClick={() => navigate("/dash/dashboard")}>
                    NX
                </span>
                {!collapsed && <span className="ds-sidebar__brand-name">NIXBNK</span>}
                {!collapsed && (
                    <button
                        type="button"
                        className="ds-collapse-btn tw-ml-auto"
                        onClick={() => setCollapsed(true)}
                        aria-label="Collapse sidebar"
                    >
                        <FontAwesomeIcon icon={faAngleLeft} />
                    </button>
                )}
            </div>

            <div className="ds-sidebar__scroll">
                {collapsed && (
                    <button
                        type="button"
                        className="ds-collapse-btn tw-mb-2"
                        style={{ marginLeft: "auto", marginRight: "auto" }}
                        onClick={() => setCollapsed(false)}
                        aria-label="Expand sidebar"
                    >
                        <FontAwesomeIcon icon={faAngleRight} />
                    </button>
                )}
                {grouped.map((group) =>
                    group.items.length ? (
                        <div key={group.label}>
                            <div className="ds-sidebar__group-label">
                                <span className="ds-sidebar__group-label-text">{group.label}</span>
                            </div>
                            {group.items.map(renderItem)}
                        </div>
                    ) : null
                )}
            </div>

            <div className="ds-sidebar__footer">
                <Link to="/logout" className="ds-nav-item xcn-link-pointer" title={collapsed ? "Logout" : undefined}>
                    <span className="ds-nav-item__icon">
                        <FontAwesomeIcon icon={faRightFromBracket} />
                    </span>
                    <span className="ds-nav-item__label">Logout</span>
                </Link>
            </div>
        </div>
    )
}
